import { useState, useEffect, useCallback } from "react";
import {
  ComposedChart, Line, Area, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { generateFakeStationSeries } from "../fakeData";
import AggregationNotice from "./AggregationNotice";
import PreDataNotice from "./PreDataNotice";
import { readAggregation, buildEnvelopes, pointInterval } from "../seriesAggregation";
import { apiFetch } from "../apiFetch";
import {
  CHART_RANGE_FLOOR,
  RANGE_PRESETS,
  customRangeError,
  preDataNotice,
  windowBounds,
} from "../chartRange";

const POLL_INTERVAL_MS = 5 * 60 * 1000;

// One compact chart per selected parameter, stacked on a shared time axis, rather
// than one chart with several curves (issue #85).
//
// The parameters cannot share a Y axis: pressure sits near 1005 hPa and wind
// speed near 1 m/s, so on a common scale everything but pressure collapses onto
// the baseline. Recharts can give each series its own YAxis via yAxisId, but each
// axis eats horizontal room, and with seven selectable parameters the gutters
// would crowd out the plot. Normalising to a shared 0-100% keeps one chart but
// takes the units off the axis, and "what was the pressure" is the question this
// panel exists to answer.
//
// Small multiples keep every parameter in its own real units at its own scale,
// stay readable at any count, and still support comparison because the X axes
// line up. The cost is vertical space, which the panel already scrolls.
const PARAMETERS = [
  { key: "air_temperature_c",  labelKey: "wsAirTemperature", unit: "°C",  colour: "#ef4444", decimals: 1 },
  { key: "humidity_pct",       labelKey: "wsHumidity",       unit: "%",   colour: "#3b82f6", decimals: 0 },
  { key: "pressure_hpa",       labelKey: "wsPressure",       unit: "hPa", colour: "#8b5cf6", decimals: 1 },
  { key: "wind_speed_ms",      labelKey: "wsWindSpeed",      unit: "m/s", colour: "#10b981", decimals: 1 },
  { key: "wind_gust_ms",       labelKey: "wsWindGust",       unit: "m/s", colour: "#047857", decimals: 1 },
  // Rain that fell in each plotted interval, from the station's counter (#243).
  // Bars, not a line: it is an amount per interval, and a line between two bars
  // would claim rain fell at a rate in between. The rain RATE is not plotted;
  // the panel above shows it as a current value.
  { key: "rain_mm",            labelKey: "wsRain",           unit: "mm",  colour: "#2563eb", decimals: 2, amount: true },
  { key: "wind_direction_deg", labelKey: "wsWindDirection",  unit: "°",   colour: "#0891b2", decimals: 0, circular: true },
  { key: "illuminance_lux",    labelKey: "wsIlluminance",    unit: "lx",  colour: "#f59e0b", decimals: 0 },
  { key: "uv_index",           labelKey: "wsUvIndex",        unit: "",    colour: "#d946ef", decimals: 0 },
];

// Opening with all seven would be a wall of charts. Temperature, humidity and
// pressure are the three an operator reads first.
const DEFAULT_SELECTED = ["air_temperature_c", "humidity_pct", "pressure_hpa"];

const COMPASS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
const compassPoint = (deg) =>
  COMPASS[Math.round((((deg % 360) + 360) % 360) / 45) % 8];

function formatTick(iso, range) {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  // A 24h range is read by time of day; anything longer by date.
  return range === "24h"
    ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

const clockTime = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

// Reads the point rather than payload[0].value, because an aggregated panel
// renders two series (the min/max band and the mean line) and the first of them
// is the band's [lo, hi] pair.
//
// A point covering 8 readings now shows the interval it covers and the extremes
// inside it, instead of asserting one value at one instant (#108).
function ParameterTooltip({ active, payload, label, param, t }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  const value = point?.[param.key];
  if (value === undefined || value === null) return null;

  const fmt = (v) => Number(v).toFixed(param.decimals);
  const interval = pointInterval(point);
  const low = point[`${param.key}_min`];
  const high = point[`${param.key}_max`];

  return (
    <div style={styles.tooltip}>
      <div style={styles.tooltipTime}>
        {interval
          ? t("aggIntervalTooltip", {
              n: interval.n,
              start: clockTime(interval.start),
              end: clockTime(interval.end),
            })
          : new Date(label).toLocaleString("en-GB")}
      </div>
      <div style={styles.tooltipValue}>
        {t(param.labelKey)}: <strong>{fmt(value)}{param.unit && ` ${param.unit}`}</strong>
        {param.circular && ` (${compassPoint(value)})`}
      </div>
      {typeof low === "number" && typeof high === "number" && (
        <div style={styles.tooltipRange}>
          {t("aggRangeTooltip", { min: fmt(low), max: fmt(high) })}
        </div>
      )}
      {point.is_latest && <div style={styles.tooltipRange}>{t("aggLatestTooltip")}</div>}
    </div>
  );
}

// `simulated` renders generated data and makes no network request — every tier
// below superuser is simulated, and fetching here would put real site
// measurements in front of them.
export default function WeatherStationChart({ deviceId, apiBaseUrl, label, t = (k) => k, simulated = false }) {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [selected, setSelected] = useState(DEFAULT_SELECTED);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [customError, setCustomError] = useState(null);
  const [committedCustom, setCommittedCustom] = useState({ from: "", to: "" });
  // "auto" lets the API decide the point budget; "raw" asks for the readings as
  // received, which the API serves or refuses with raw_capped (#108).
  const [plotMode, setPlotMode] = useState("auto");
  const [agg, setAgg] = useState(() => readAggregation(null));
  // When this station started reporting, from the API (#184).
  const [firstReading, setFirstReading] = useState(null);

  // Same options and same custom-range rules as TowerDriftChart, so the two
  // charts do not disagree about what "7d" means.
  // From the shared list so all four charts offer the same set (#231).
  const RANGE_OPTIONS = RANGE_PRESETS.map(({ key, value }) => ({ label: t(key), value }));

  const toggleParameter = (key) =>
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const buildUrl = () => {
    const base = `${apiBaseUrl}/api/station/series?device_id=${encodeURIComponent(deviceId)}`;
    // Only appended when raw was actually asked for, so the default request is
    // byte-for-byte the one this endpoint has always received.
    const raw = plotMode === "raw" ? "&raw=1" : "";
    if (range === "custom" && committedCustom.from && committedCustom.to) {
      return `${base}&start=${committedCustom.from}&end=${committedCustom.to}${raw}`;
    }
    return `${base}&range=${range}${raw}`;
  };

  // Custom is selected but Apply has not been pressed on a complete pair of dates
  // yet, so there is no range to ask for. buildUrl() falls through to
  // `range=custom`, and station.mjs rejects that with a 400 — the same fault #91
  // fixed in TowerDriftChart, inherited here because this component copied its
  // range selector. Falling back to a default range instead would be worse: it
  // charts 30 days while the user is looking at an empty Custom form.
  //
  // Unlike TowerDriftChart this is qualified by !simulated. There is no request
  // to reject in simulated mode, and the generator deliberately draws a custom
  // range as 30d so the picker still does something for a non-superuser rather
  // than appearing broken.
  const customPending = range === "custom" && !(committedCustom.from && committedCustom.to);
  const blockFetch = customPending && !simulated;

  // This station went live on 2026-07-30, more than six months after the oldest
  // device, so a full-range window is mostly blank for it. Unlabelled, that
  // blank reads as an outage (#184).
  const windowStart = windowBounds(range, committedCustom);
  const preData = preDataNotice(firstReading, windowStart.from, windowStart.to);

  const fetchData = useCallback(async () => {
    if (blockFetch) { setError(null); setLoading(false); return; }
    try {
      let json;
      if (simulated) {
        // The generator works from the fixed range presets. A custom range is
        // drawn as 30d rather than being unsupported, so the picker still does
        // something for a non-superuser instead of appearing broken.
        json = generateFakeStationSeries(deviceId, range === "custom" ? "30d" : range);
      } else {
        const res = await apiFetch(buildUrl());
        if (!res.ok) throw new Error(`API error ${res.status}`);
        json = await res.json();
      }
      // The min/max envelope is turned into the [lo, hi] pairs the band renders
      // from once, here, rather than per panel on every draw.
      setData(buildEnvelopes(json.data ?? [], PARAMETERS.map((p) => p.key)));
      setAvailableFields(json.available_fields ?? json.fields ?? []);
      // Simulated data describes no real deployment, so it must not claim an
      // install date.
      setFirstReading(simulated ? null : (json.first_reading ?? null));
      setAgg(readAggregation(json));
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [deviceId, apiBaseUrl, range, committedCustom, blockFetch, simulated, plotMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // No poll either, or the interval would fire the same rejected request every
    // POLL_INTERVAL_MS while the user is still filling in the dates.
    if (blockFetch) { setLoading(false); return undefined; }
    setLoading(true);
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData, blockFetch]);

  // Only offer a parameter the range actually contains, so toggling one on can
  // never produce an empty chart.
  const offered = PARAMETERS.filter(
    (p) => availableFields.length === 0 || availableFields.includes(p.key)
  );
  const shown = offered.filter((p) => selected.includes(p.key));

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>{t("wsHistoryTitle")}</h2>
          <p style={styles.subtitle}>{label ?? deviceId}</p>
        </div>
      </div>

      {/* Range selector — same set as the drift chart */}
      <div style={styles.rangeRow}>
        {RANGE_OPTIONS.map((opt) => (
          <button key={opt.value} onClick={() => setRange(opt.value)}
            style={range === opt.value ? styles.rangeActive : styles.rangeBtn}>
            {opt.label}
          </button>
        ))}
        <button onClick={() => setRange("custom")}
          style={range === "custom" ? styles.rangeActive : styles.rangeBtn}>
          {t("rangeCustom")}
        </button>
        <span style={styles.lastUpdated}>
          {lastUpdated ? `${t("updated")} ${lastUpdated.toLocaleTimeString()}` : t("loading")}
        </span>
      </div>

      {range === "custom" && (
        <div style={styles.customRangeRow}>
          <label style={styles.customLabel}>{t("customFrom")}</label>
          <input type="date" value={customFrom}
            min={CHART_RANGE_FLOOR}
            max={customTo || new Date().toISOString().split("T")[0]}
            onChange={(e) => { setCustomFrom(e.target.value); setCustomError(null); }}
            style={styles.dateInput}
          />
          {/* One click back to the start of the data (#231). Sets the field and
              stops there rather than applying, so a long window is never
              requested before the operator can see what it chose. */}
          <button type="button" style={styles.fromStartBtn}
            title={t("fromStartTitle")}
            onClick={() => { setCustomFrom(CHART_RANGE_FLOOR); setCustomError(null); }}>
            {t("fromStart")}
          </button>
          <label style={styles.customLabel}>{t("customTo")}</label>
          <input type="date" value={customTo} min={customFrom || CHART_RANGE_FLOOR}
            max={new Date().toISOString().split("T")[0]}
            onChange={(e) => { setCustomTo(e.target.value); setCustomError(null); }}
            style={styles.dateInput}
          />
          <button style={styles.applyBtn} onClick={() => {
            const key = customRangeError(customFrom, customTo);
            if (key) { setCustomError(t(key)); return; }
            setCustomError(null);
            setCommittedCustom({ from: customFrom, to: customTo });
          }}>
            {t("apply")}
          </button>
          {customError && <span style={styles.customError}>{customError}</span>}
        </div>
      )}

      {/* Parameter selector. Checkboxes rather than tabs so several can be shown
          at once, matching the drift chart's axis toggles. */}
      <div style={styles.paramToggles}>
        {offered.map((p) => (
          <label key={p.key} style={styles.paramToggle}>
            <input type="checkbox" checked={selected.includes(p.key)}
              onChange={() => toggleParameter(p.key)} />
            <span style={{ ...styles.swatch, background: p.colour }} />
            <span style={{ marginLeft: 2 }}>{t(p.labelKey)}</span>
          </label>
        ))}
      </div>

      {/* Why the chart is blank before a given date, when it is. Not gated on
          data.length: the case most in need of explaining is the empty one. */}
      {!loading && !error && !customPending && (
        <PreDataNotice notice={preData} t={t} />
      )}

      {/* What the points are, and the raw/aggregated control. Renders nothing
          when the series was not decimated. */}
      {!loading && !error && !customPending && data.length > 0 && (
        <AggregationNotice
          agg={agg}
          mode={plotMode}
          onModeChange={setPlotMode}
          t={t}
          canToggle={!simulated}
        />
      )}

      {loading ? (
        <div style={styles.placeholder}>{t("driftChartLoading")}</div>
      ) : error ? (
        <div style={styles.errorBox}>{t("driftChartError")}: {error}</div>
      ) : customPending ? (
        // Not "No data" — nothing has been asked for yet. Saying no data exists
        // for a range the user has not finished specifying is a false statement
        // about the station.
        <div style={styles.placeholder}>{t("enterBothDates")}</div>
      ) : data.length === 0 ? (
        <div style={styles.placeholder}>{t("driftChartNoData")}</div>
      ) : shown.length === 0 ? (
        <div style={styles.placeholder}>{t("wsNoParameterSelected")}</div>
      ) : (
        <div>
          {shown.map((p) => (
            <div key={p.key} style={styles.panel}>
              <div style={styles.panelHead}>
                <span style={{ ...styles.swatch, background: p.colour }} />
                <span style={styles.panelLabel}>{t(p.labelKey)}</span>
                {p.unit && <span style={styles.panelUnit}>{p.unit}</span>}
              </div>
              <ResponsiveContainer width="100%" height={p.circular ? 150 : 130}>
                <ComposedChart data={data} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="ts"
                    tickFormatter={(v) => formatTick(v, range)}
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    minTickGap={40}
                    stroke="#e5e7eb"
                  />
                  {/* A bearing is fixed to 0-360 with compass ticks; everything
                      else is auto-scaled to its own values, which is the point of
                      giving each parameter its own panel. */}
                  <YAxis
                    domain={p.circular ? [0, 360] : p.amount ? [0, "auto"] : ["auto", "auto"]}
                    ticks={p.circular ? [0, 90, 180, 270, 360] : undefined}
                    tickFormatter={p.circular ? (v) => (v === 360 ? "N" : compassPoint(v)) : undefined}
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    width={48}
                    stroke="#e5e7eb"
                  />
                  <Tooltip content={<ParameterTooltip param={p} t={t} />} />
                  {/* The min/max envelope of each aggregated point, drawn under
                      the line: the band is what the station actually measured
                      across the interval, the line is the mean through it. A
                      gust that peaked inside one bucket is no longer averaged
                      into a wind speed that never blew (#108).
                      Not drawn for a bearing — min/max on a circular quantity
                      would bound 350 and 10 degrees the wrong way round, so the
                      API sends no envelope for it either. */}
                  {!p.circular && !p.amount && (
                    <Area dataKey={`${p.key}_band`} stroke="none" fill={p.colour}
                      fillOpacity={0.16} connectNulls={false} activeDot={false}
                      isAnimationActive={false} />
                  )}
                  {p.amount ? (
                    <Bar dataKey={p.key} fill={p.colour} isAnimationActive={false} />
                  ) : p.circular ? (
                    // Dots, no connecting line. A bearing wandering across north
                    // would otherwise draw a full-height vertical stroke between
                    // 359 and 1 degrees, which reads as a violent swing rather
                    // than the one-degree change it is.
                    <Line type="monotone" dataKey={p.key} stroke="none"
                      dot={{ r: 1.6, fill: p.colour, stroke: "none" }} isAnimationActive={false} />
                  ) : (
                    <Line type="monotone" dataKey={p.key} stroke={p.colour}
                      dot={false} strokeWidth={1.5} connectNulls={false}
                      isAnimationActive={false} />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
              {p.circular && <p style={styles.circularNote}>{t("wsWindDirectionNote")}</p>}
            </div>
          ))}
        </div>
      )}

      <p style={styles.footnote}>{t("wsChartRainNote")}</p>
    </div>
  );
}

const styles = {
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "20px 24px", fontFamily: "'Inter', system-ui, sans-serif", maxWidth: 1600 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 },
  title: { margin: 0, fontSize: 18, fontWeight: 600, color: "#111827" },
  subtitle: { margin: "2px 0 0", fontSize: 12, color: "#9ca3af" },
  rangeRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" },
  rangeBtn: { padding: "4px 12px", border: "1px solid #d1d5db", borderRadius: 6, background: "#f9fafb", color: "#374151", cursor: "pointer", fontSize: 13 },
  rangeActive: { padding: "4px 12px", border: "1px solid #3b82f6", borderRadius: 6, background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  lastUpdated: { marginLeft: "auto", fontSize: 12, color: "#9ca3af" },
  customRangeRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" },
  customLabel: { fontSize: 12, color: "#6b7280" },
  dateInput: { padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, color: "#374151", background: "#f9fafb" },
  applyBtn: { padding: "4px 14px", border: "1px solid #3b82f6", borderRadius: 6, background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  // Quieter than applyBtn on purpose: this fills a field, it does not run the
  // query, and it must not compete with Apply for the eye (#231).
  fromStartBtn: { padding: "4px 10px", border: "1px solid #d1d5db", borderRadius: 6, background: "#f9fafb", color: "#6b7280", cursor: "pointer", fontSize: 12 },
  customError: { fontSize: 12, color: "#ef4444" },
  paramToggles: { display: "flex", gap: 14, marginBottom: 14, alignItems: "center", flexWrap: "wrap" },
  paramToggle: { display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: 13, color: "#374151", userSelect: "none" },
  swatch: { display: "inline-block", width: 10, height: 10, borderRadius: 2, flexShrink: 0 },
  panel: { borderTop: "1px solid #f3f4f6", paddingTop: 8, marginBottom: 4 },
  panelHead: { display: "flex", alignItems: "center", gap: 6, marginBottom: 2 },
  panelLabel: { fontSize: 13, fontWeight: 600, color: "#374151" },
  panelUnit: { fontSize: 11, color: "#9ca3af" },
  placeholder: { height: 140, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 14 },
  errorBox: { height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444", fontSize: 14, background: "#fef2f2", borderRadius: 6 },
  tooltip: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 10px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
  tooltipTime: { fontSize: 11, color: "#9ca3af", marginBottom: 2 },
  tooltipValue: { fontSize: 12, color: "#374151" },
  tooltipRange: { fontSize: 11, color: "#6b7280", marginTop: 2 },
  circularNote: { margin: "0 0 4px 48px", fontSize: 11, color: "#9ca3af" },
  footnote: { marginTop: 12, marginBottom: 0, fontSize: 11, color: "#9ca3af", lineHeight: 1.5 },
};
